import * as React from "react";
import { useRef, useState, useMemo } from "react";
import { Input, Modal } from "react-ui";
import { useDebounce } from "use-debounce";

import { PicklistDataItem } from "../../../../../Engine/Core/Picklists/PicklistTypes";
import { emptyRef } from "../../../../TypingUtils";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { TreePicklistProps } from "../TreePicklist";
import { Loader } from "../../Common/Loader/Loader";
import { FilterBuilder } from "../../Common/FilterBuilder";
import { TreePicklistItemResponse, TreeQuery } from "../TreePicklistItemProvider";
import { IterUtils } from "../../../../../../../Common/IterableUtils";
import { isNotNullOrEmpty } from "../../../../../../../Common/TypingUtils";
import { WIDTH_MODAL } from "../../Common/Styles/PicklistModal.styles";
import { getCandyHub } from "../../../../../Forms/Sentry/InitializeSentry";
import { TREE_PICKLIST_RESET_VALUES, TREE_PICKLIST_SEARCH_MODAL_INPUT } from "../../../../metricsAction";
import { useMetrics } from "../../../../Internals/Metrics/MetricsProviderContext";
import { filterUnnecessaryCheckedChildren } from "../TreePicklistNode/utils/filterUnnecessaryCheckedChildren";
import { useResetNodesStateCallbacks } from "../TreePicklistNode/utils/useResetCallbacks";
import { TreePicklistItem } from "../TreePicklistItem";

import { SearchInput } from "./SearchInput";
import { Body, Footer, Header, LoadingErrorMessage, NotFoundMessage, ResetBlock } from "./ModalBlocks";
import { MultipleTree } from "./MultipleTree";
import { SingleTree } from "./SingleTree";
import { useTreePicklistItemFabric } from "./useTreePicklistItemFabric";

export interface TreeQueryResponse {
    items: TreePicklistItemResponse[];
    suitableCount: number;
}

export interface PicklistModalProps extends TreePicklistProps {
    onClose: () => void;
    onSave: (items: PicklistDataItem[]) => void;
    treeQuery: TreeQuery;
}

const enum ModalStatus {
    Idle,
    Loading,
    Saving,
    LoadingError,
}

export const TreePicklistModal = (props: PicklistModalProps): JSX.Element => {
    const input = useRef<Input>(emptyRef);

    const [status, setStatus] = useState<ModalStatus>(ModalStatus.Idle);
    const isLoading = status === ModalStatus.Loading;
    const isSaving = status === ModalStatus.Saving;
    const isPending = isLoading || isSaving;
    const isLoadingError = status === ModalStatus.LoadingError;

    const [searchValue, setSearchValue] = useState("");
    const [searchValueToExecuteQuery] = useDebounce(searchValue, 500);

    const [nodesToSave, setNodesToSave] = useState<TreePicklistItem[]>([]);

    const [needNodesToOpen, setNeedNodesToOpen] = useState<TreePicklistItem[]>([]);

    const isNotFound = Boolean(searchValueToExecuteQuery && !needNodesToOpen.length && !isLoading);
    const isSomeElementsPicked = !isSaving && nodesToSave.length > 0;

    const track = useMetrics();

    const createTreePicklistItem = useTreePicklistItemFabric(props);
    const rootElements = useRootElements(props.childColumnName, treeQueryResponse, createTreePicklistItem);

    useAsyncEffect(async signal => {
        if (isNotNullOrEmpty(props.value)) {
            await fetchAndSetElementsToOpen(props.multiple ? props.value.split(", ") : [props.value], signal);
        }
    }, []);

    useAsyncEffect(
        async signal => {
            track(TREE_PICKLIST_SEARCH_MODAL_INPUT, {
                value: searchValueToExecuteQuery,
                gid: props.gId,
            });

            if (isNotNullOrEmpty(searchValueToExecuteQuery)) {
                await fetchAndSetElementsToOpen([searchValueToExecuteQuery], signal);
            }
        },
        [searchValueToExecuteQuery]
    );

    const { resetNodeStateManager: resetNodeManager, resetStateForAllNodes } = useResetNodesStateCallbacks();

    const fetchAndSetElementsToOpen = async (idsOfElementsToOpen: string[], signal: AbortSignal) => {
        setNeedNodesToOpen([]);

        const filterBuilder = new FilterBuilder().column(props.searchByField).in(idsOfElementsToOpen);
        const response = await treeQueryResponse(filterBuilder);

        if (signal.aborted) {
            return;
        }

        const treeItems = createTreeItemsFromResponseItems(response.items);
        const pickedTreeItems = treeItems.filter(item => idsOfElementsToOpen.includes(item.elementId));

        setNodesToSave(prev => [...prev, ...pickedTreeItems]);
        setNeedNodesToOpen(prev => [...prev, ...treeItems]);
    };

    async function treeQueryResponse(filterBuilder: FilterBuilder): Promise<TreeQueryResponse> {
        try {
            setStatus(ModalStatus.Loading);

            const response = await props.treeQuery({
                searchQuery: searchValueToExecuteQuery,
                nodeFilter: filterBuilder,
            });

            setStatus(ModalStatus.Idle);

            return response;
        } catch (e) {
            setStatus(ModalStatus.LoadingError);
            getCandyHub().captureException(e);

            const errorResponse: TreeQueryResponse = { suitableCount: 0, items: [] };
            return errorResponse;
        }
    }

    const createTreeItemsFromResponseItems = (responseItems: TreePicklistItemResponse[]) =>
        responseItems.map(item => {
            const ancestors = resolveAncestors({
                descendantItem: item,
                responseItems: responseItems,
                childColumnName: props.childColumnName,
                parentColumnName: props.parentColumnName,
            });

            return createTreePicklistItem(item, ancestors);
        });

    const handleBodyKeyEvent = () => {
        input.current?.focus();
    };

    const handleSaveAndClose = async () => {
        try {
            setStatus(ModalStatus.Saving);

            const { searchByField } = props;
            const totalValue = props.multiple
                ? await filterUnnecessaryCheckedChildren(nodesToSave)
                : nodesToSave.map(item => item.toOriginal());

            const selectedItems = IterUtils.sortBy(totalValue ?? [], x => x[searchByField] ?? "");
            props.onSave(selectedItems.length > 0 ? selectedItems : [{ [searchByField]: "" }]);

            setStatus(ModalStatus.Idle);
        } catch (e) {
            setStatus(ModalStatus.LoadingError);
            getCandyHub().captureException(e);
        }
    };

    const resetNodesToSave = () => {
        track(TREE_PICKLIST_RESET_VALUES, { gid: props.gId });
        resetStateForAllNodes();
        setNodesToSave([]);
    };

    const renderNodes = () => {
        if (props.multiple) {
            return (
                <MultipleTree
                    items={rootElements}
                    searchQuery={searchValueToExecuteQuery}
                    modalProps={props}
                    onLoadingError={() => setStatus(ModalStatus.LoadingError)}
                    nodesToSave={nodesToSave}
                    setNodesToSave={setNodesToSave}
                    needNodesToOpen={needNodesToOpen}
                    resetNodeStateManager={resetNodeManager}
                />
            );
        }

        return (
            <SingleTree
                items={rootElements}
                searchQuery={searchValueToExecuteQuery}
                modalProps={props}
                onLoadingError={() => setStatus(ModalStatus.LoadingError)}
                needNodesToOpen={needNodesToOpen}
            />
        );
    };

    return (
        <Modal ignoreBackgroundClick width={WIDTH_MODAL} onClose={props.onClose} disableClose={isSaving}>
            <Header title={props.title} description={props.descriptionInModal}>
                <SearchInput ref={input} searchValue={searchValue} onSearchValueChange={setSearchValue} />
            </Header>
            <Body onKeyDown={handleBodyKeyEvent}>
                {isSomeElementsPicked && <ResetBlock onReset={resetNodesToSave} />}

                {isLoadingError ? (
                    <LoadingErrorMessage />
                ) : (
                    <Loader active={isPending} caption={isSaving ? "Сохраняем..." : undefined}>
                        {isNotFound ? <NotFoundMessage searchValue={searchValueToExecuteQuery} /> : renderNodes()}
                    </Loader>
                )}
            </Body>
            {!isPending && (
                <Footer
                    multiple={Boolean(props.multiple)}
                    onSaveAndClose={handleSaveAndClose}
                    onCloseModal={props.onClose}
                />
            )}
        </Modal>
    );
};

const useRootElements = (
    childColumnName: string,
    queryTree: (filter: FilterBuilder) => Promise<TreeQueryResponse>,
    createTreePicklistItem: (item: TreePicklistItemResponse, ancestors: TreePicklistItemResponse[]) => TreePicklistItem
): TreePicklistItem[] => {
    const [nodeList, setNodeList] = React.useState<TreePicklistItemResponse[]>([]);
    const treeItems = useMemo(
        // корневые элементы создаются без предков
        () => nodeList.map(item => createTreePicklistItem(item, [])),
        [createTreePicklistItem, nodeList]
    );

    useAsyncEffect(async signal => {
        const filterBuilder = new FilterBuilder().column(childColumnName).eql("");
        const response = await queryTree(filterBuilder);
        if (signal.aborted) {
            return;
        }
        setNodeList(response.items);
    }, []);

    return treeItems;
};

interface ResolveAncestorsParams {
    descendantItem: TreePicklistItemResponse;
    responseItems: TreePicklistItemResponse[];
    parentColumnName: string;
    childColumnName: string;
}

const resolveAncestors = ({
    descendantItem,
    responseItems,
    parentColumnName,
    childColumnName,
}: ResolveAncestorsParams) => {
    const ancestors: TreePicklistItemResponse[] = [];
    const itemsToTraverse = [...responseItems];
    let currentChild = descendantItem;

    let currentItemIdx = 0;
    const totalItemsCount = itemsToTraverse.length;

    while (currentItemIdx < totalItemsCount) {
        const currentItem = itemsToTraverse[currentItemIdx];
        const isParentItem = currentItem?.[parentColumnName] === currentChild[childColumnName];

        if (currentItem && isParentItem) {
            ancestors.push(currentItem);
            currentChild = currentItem;
            currentItemIdx = 0;
            continue;
        }
        currentItemIdx++;
    }

    return ancestors;
};
